import { useState, useEffect, useMemo } from 'react';
import { api } from '../api';
import toast from 'react-hot-toast';
import NavIcon from '../components/NavIcon';
import { confirmDialog } from '../components/ConfirmDialog';

function Modal({ shortcut, products, onClose, onSave }) {
  const [form, setForm] = useState(shortcut || { productId: products[0]?.id || '', shortcut: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.productId || !form.shortcut.trim()) { toast.error('Fill in both fields'); return; }
    setSaving(true);
    try {
      if (shortcut) {
        await api.updateShortcut(shortcut.id, { productId: Number(form.productId), shortcut: form.shortcut });
      } else {
        await api.createShortcut({ productId: Number(form.productId), shortcut: form.shortcut });
      }
      toast.success(shortcut ? 'Shortcut updated' : 'Shortcut added');
      onSave();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{shortcut ? 'Edit Shortcut' : 'New Shortcut'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="field"><label>Product *</label>
            <select value={form.productId} onChange={e => set('productId', e.target.value)}>
              <option value="">Select product</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Shortcut *</label>
            <input
              value={form.shortcut}
              onChange={e => set('shortcut', e.target.value)}
              placeholder="e.g. c5"
              autoFocus
            />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-success" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Shortcuts() {
  const [shortcuts, setShortcuts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | shortcut object

  const load = async () => {
    try {
      const [sc, prod] = await Promise.all([api.getShortcuts(), api.getProductsLookup()]);
      setShortcuts(sc);
      setProducts(prod);
    } catch {
      toast.error('Failed to load shortcuts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (s) => {
    const next = !s.isActive;
    if (!(await confirmDialog(`${next ? 'Reactivate' : 'Deactivate'} shortcut ".${s.shortcut}"?`, { danger: !next, confirmText: next ? 'Reactivate' : 'Deactivate' }))) return;
    try {
      await api.setShortcutActive(s.id, next);
      toast.success(next ? 'Reactivated' : 'Deactivated');
      load();
    } catch { toast.error('Failed to update'); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return shortcuts;
    return shortcuts.filter(s => s.shortcut.toLowerCase().includes(q) || s.productName?.toLowerCase().includes(q));
  }, [shortcuts, search]);

  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;

  return (
    <div className="page">
      <div className="page-header flex-between">
        <div>
          <h2>Product Shortcuts</h2>
          <p className="page-sub">Assign a quick-note shortcut (e.g. "c5") to each product, for use on the Quick Notes page</p>
        </div>
        <button className="btn-primary" onClick={() => setModal('add')}>+ New Shortcut</button>
      </div>

      <div className="filter-bar">
        <input placeholder="Search shortcuts or products…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state"><div className="icon"><NavIcon name="clipboard" size={32} /></div><div>No shortcuts found.</div></div>
      ) : (
        <div className="card table-wrap">
          <table>
            <thead><tr><th>Shortcut</th><th>Product</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} style={s.isActive ? undefined : { opacity: 0.55 }}>
                  <td className="mono" data-label="Shortcut">.{s.shortcut}</td>
                  <td data-label="Product">{s.productName || '—'}</td>
                  <td data-label="Status">
                    <span className={`badge ${s.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {s.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td data-label=""><div className="flex gap-2">
                    <button className="btn btn-secondary btn-sm" onClick={() => setModal(s)}>Edit</button>
                    <button
                      className={`btn btn-sm ${s.isActive ? 'btn-danger' : 'btn-secondary'}`}
                      onClick={() => toggleActive(s)}
                    >
                      {s.isActive ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal
          shortcut={modal === 'add' ? null : modal}
          products={products}
          onClose={() => setModal(null)}
          onSave={load}
        />
      )}
    </div>
  );
}
